import { api } from '../../lib/api-client';

export type QuotationStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'SENT' | 'ACCEPTED' | 'EXPIRED' | 'CANCELLED';

export type QuotationLineType =
  'BIKE' | 'ACCESSORY' | 'INSURANCE' | 'ROAD_TAX' | 'REGISTRATION' | 'SERVICE' | 'OTHER';

/**
 * Money is a STRING everywhere on this boundary.
 *
 * Turning it into a JS number to display it would reintroduce exactly the
 * floating-point error the API went to the trouble of avoiding — and the number
 * on screen is the one the customer reads.
 */
export interface QuotationLine {
  uid: string;
  type: QuotationLineType;
  description: string;
  qty: string;
  unitPrice: string;
  discountAmount: string;
  taxCode: string;
  lineTotal: string;
  itemCode: string | null;
  skuUid: string | null;
}

export interface Quotation {
  uid: string;
  quotationNo: string;
  status: QuotationStatus;
  customerUid: string;
  customerName: string;
  leadUid: string | null;
  model: string | null;
  validUntil: string;
  isExpired: boolean;
  subtotal: string;
  discountTotal: string;
  discountPercent: string;
  taxTotal: string;
  grandTotal: string;
  currency: string;
  terms: string | null;
  sentAt: string | null;
  needsDiscountApproval: boolean;
  lines: QuotationLine[];
  branchCode: string | null;
  createdAt: string;
}

export interface QuotationPage {
  items: Quotation[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface QuotationLineInput {
  type: QuotationLineType;
  description: string;
  qty: string;
  /** Omitted for BIKE and ACCESSORY: the server prices those from AutoCount. */
  unitPrice?: string;
  discountAmount?: string;
  itemCode?: string;
  skuUid?: string;
}

export interface BikeModelOption {
  uid: string;
  label: string;
}

export interface BikeVariant {
  itemCode: string;
  description: string;
  listPrice: string;
}

export interface AccessoryOption {
  uid: string;
  skuCode: string;
  name: string;
  sellPrice: string;
}

export function listBikeModels(q: string): Promise<BikeModelOption[]> {
  return api<BikeModelOption[]>(`/sales/bike-models?q=${encodeURIComponent(q.slice(0, 100))}`);
}

export function listBikeVariants(modelUid: string): Promise<BikeVariant[]> {
  return api<BikeVariant[]>(`/sales/bike-variants?modelUid=${encodeURIComponent(modelUid)}`);
}

export function listAccessories(q: string): Promise<AccessoryOption[]> {
  return api<AccessoryOption[]>(`/sales/accessories?q=${encodeURIComponent(q.slice(0, 100))}`);
}

export function listQuotations(params: {
  q?: string;
  status?: QuotationStatus;
  cursor?: string;
}): Promise<QuotationPage> {
  const search = new URLSearchParams();
  if (params.q !== undefined && params.q.length > 0) search.set('q', params.q);
  if (params.status !== undefined) search.set('status', params.status);
  if (params.cursor !== undefined && params.cursor.length > 0) search.set('cursor', params.cursor);
  const qs = search.toString();
  return api<QuotationPage>(`/quotations${qs.length > 0 ? `?${qs}` : ''}`);
}

export function getQuotation(uid: string): Promise<Quotation> {
  return api<Quotation>(`/quotations/${uid}`);
}

export function createQuotation(input: {
  leadUid?: string;
  customerUid?: string;
  itemCode?: string;
  lines?: QuotationLineInput[];
  validForDays?: number;
  terms?: string;
}): Promise<Quotation> {
  return api<Quotation>('/quotations', { method: 'POST', body: input });
}

export function replaceLines(uid: string, lines: QuotationLineInput[]): Promise<Quotation> {
  return api<Quotation>(`/quotations/${uid}/lines`, { method: 'PUT', body: { lines } });
}

export function sendQuotation(uid: string): Promise<Quotation> {
  return api<Quotation>(`/quotations/${uid}/transitions/send`, { method: 'POST', body: {} });
}

export function approveDiscount(uid: string, reason: string): Promise<Quotation> {
  return api<Quotation>(`/quotations/${uid}/transitions/approve-discount`, {
    method: 'POST',
    body: { reason },
  });
}

export function cancelQuotation(uid: string, reason: string): Promise<Quotation> {
  return api<Quotation>(`/quotations/${uid}/transitions/cancel`, { method: 'POST', body: { reason } });
}
